
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Eye, Pencil, Calendar, User, ImageOff, LayoutTemplate, Video, ChevronDown, ChevronUp, Settings } from "lucide-react";
import { extractVideoFromContent } from "@/utils/videoContent";
import { BlogPost, BlogPostFormData, BlogCategory } from "@/types/blogPost";
import { fetchBlogCategories } from "@/utils/blogOperations";
import { settingsApi } from "@/integrations/mysql/client";
import { loadCustomTemplates, type CustomTemplate } from "@/components/BlogTemplateManager";
import { BLOG_TEMPLATES } from "@/utils/blogTemplates";
import { useToast } from "@/hooks/use-toast";
import { RichTextEditor } from "./RichTextEditor";
import { BlogPostScheduler } from "./BlogPostScheduler";
import { BlogContent } from "./BlogContent";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";


// ── Video helpers ──────────────────────────────────────────────────────────────
function getYouTubeEmbedUrl(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&]+)/,
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?&]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  return null;
}

function isInstagram(url: string) { return /instagram\.com/.test(url); }
function isTikTok(url: string)    { return /tiktok\.com/.test(url); }

function VideoEmbed({ url }: { url: string }) {
  const ytEmbed = getYouTubeEmbedUrl(url);
  if (ytEmbed) {
    return (
      <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm aspect-video">
        <iframe src={ytEmbed} className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen />
      </div>
    );
  }
  if (isInstagram(url) || isTikTok(url)) {
    const platform = isInstagram(url) ? "Instagram" : "TikTok";
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-3 bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3 hover:bg-brand-primary/10 transition-colors">
        <Video className="w-5 h-5 text-brand-primary shrink-0" />
        <div>
          <p className="text-sm font-semibold text-brand-primary">Abrir vídeo no {platform}</p>
          <p className="text-xs text-gray-400 truncate max-w-xs">{url}</p>
        </div>
      </a>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm aspect-video">
      <video src={url} controls className="w-full h-full object-cover" />
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────
interface BlogPostFormProps {
  isEditing: boolean;
  editingPost: BlogPost | null;
  onSubmit: (formData: BlogPostFormData) => Promise<void>;
  onCancel: () => void;
}

export function BlogPostForm({ isEditing, editingPost, onSubmit, onCancel }: BlogPostFormProps) {
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [formData, setFormData] = useState<BlogPostFormData>({
    title: "",
    subtitle: "",
    content: "",
    content_formatted: null,
    excerpt: "",
    featured_image_url: "",
    category_id: "",
    published: false,
    scheduled_for: undefined,
    video_url: "",
  });
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [showTemplates, setShowTemplates] = useState(!isEditing);
  const { toast } = useToast();

  useEffect(() => {
    if (editingPost) {
      // Extract video URL that was embedded in the content string
      const { clean, videoUrl } = extractVideoFromContent(editingPost.content || "");
      setFormData({
        title: editingPost.title || "",
        subtitle: editingPost.subtitle || "",
        content: clean,
        content_formatted: editingPost.content_formatted || null,
        excerpt: editingPost.excerpt || "",
        featured_image_url: editingPost.featured_image_url || "",
        category_id: editingPost.category_id || "",
        published: editingPost.published || false,
        scheduled_for: editingPost.scheduled_for || undefined,
        video_url: videoUrl,
      });
      setShowTemplates(false);
    } else {
      setFormData({
        title: "",
        subtitle: "",
        content: "",
        content_formatted: null,
        excerpt: "",
        featured_image_url: "",
        category_id: categories[0]?.id || "",
        published: false,
        scheduled_for: undefined,
        video_url: "",
      });
      setShowTemplates(true);
    }
  }, [editingPost, categories]);

  useEffect(() => {
    loadCategories();
    settingsApi.list().then((list: any[]) => {
      const row = list.find((s: any) => s.key === "blog_post_templates");
      const saved = loadCustomTemplates(row?.value);
      if (saved.length > 0) {
        setCustomTemplates(saved);
      } else {
        // Fallback: show built-in templates before admin visits the Templates tab
        setCustomTemplates(BLOG_TEMPLATES.map(t => ({
          id: t.id,
          name: t.label,
          description: t.description,
          content: t.content,
        })));
      }
    });
  }, []);

  const loadCategories = async () => {
    try {
      const data = await fetchBlogCategories();
      setCategories(data);
      if (!formData.category_id && data.length > 0 && !editingPost) {
        setFormData(prev => ({ ...prev, category_id: data[0].id }));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({ title: "Erro", description: "Erro ao carregar categorias.", variant: "destructive" });
    }
  };

  const applyTemplate = (tpl: BlogTemplate) => {
    // Try to find a matching category
    let categoryId = formData.category_id;
    if (tpl.category) {
      const match = categories.find(c => c.name.toLowerCase().includes(tpl.category!));
      if (match) categoryId = match.id;
    }
    setFormData(prev => ({
      ...prev,
      content: tpl.content,
      content_formatted: null, // reset so RichTextEditor re-initializes
      excerpt: tpl.excerpt,
      category_id: categoryId,
    }));
    setShowTemplates(false);
  };

  // Use functional updates so TipTap's stale callback never overwrites other fields
  const handleContentChange = (content: string, formatted: any) => {
    setFormData(prev => ({ ...prev, content, content_formatted: formatted }));
  };

  const handleScheduleChange = (scheduledDate: string | null) => {
    setFormData(prev => ({ ...prev, scheduled_for: scheduledDate || undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    if (!isEditing) {
      setFormData({
        title: "",
        subtitle: "",
        content: "",
        content_formatted: null,
        excerpt: "",
        featured_image_url: "",
        category_id: categories[0]?.id || "",
        published: false,
        scheduled_for: undefined,
        video_url: "",
      });
      setShowTemplates(true);
    }
  };

  const selectedCategory = categories.find((c) => c.id === formData.category_id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          {isEditing ? "Editar Post" : "Criar Novo Post"}
        </h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            <button type="button" onClick={() => setTab("edit")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === "edit" ? "bg-brand-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}>
              <Pencil className="w-3.5 h-3.5" />
              Editar
            </button>
            <button type="button" onClick={() => setTab("preview")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === "preview" ? "bg-brand-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}>
              <Eye className="w-3.5 h-3.5" />
              Pré-visualização
            </button>
          </div>
          {isEditing && (
            <Button onClick={onCancel} variant="outline" size="sm">Cancelar</Button>
          )}
        </div>
      </div>

      {/* ── Template picker ─────────────────────────────────────────── */}
      {tab === "edit" && (
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowTemplates(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <LayoutTemplate className="w-4 h-4 text-brand-primary" />
              <span className="text-sm font-semibold text-gray-700">Templates de post</span>
              <span className="text-xs text-gray-400">— escolha um modelo para começar mais rápido</span>
            </div>
            {showTemplates
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>

          {showTemplates && (
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {customTemplates.map(tpl => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => applyTemplate({ id: tpl.id, label: tpl.name, description: tpl.description, content: tpl.content })}
                    className="text-left border border-gray-200 rounded-xl p-4 hover:border-brand-primary hover:bg-brand-primary/3 transition-all group"
                  >
                    <p className="font-semibold text-sm text-gray-800 group-hover:text-brand-primary mb-1">{tpl.name}</p>
                    {tpl.description && <p className="text-xs text-gray-400 leading-relaxed">{tpl.description}</p>}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowTemplates(false)}
                  className="text-left border border-dashed border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-all"
                >
                  <p className="font-semibold text-sm text-gray-500 mb-1">Em branco</p>
                  <p className="text-xs text-gray-400">Começar do zero sem nenhum template</p>
                </button>
              </div>
              <div className="flex justify-end">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <Settings className="w-3 h-3" />
                  Gerencie seus templates na aba "Templates" do blog
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Preview panel */}
      {tab === "preview" && (
        <div className="border border-gray-200 rounded-2xl overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
            <Eye className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-xs text-gray-500 font-medium">Como ficará quando publicado</span>
          </div>

          <article className="p-6 sm:p-10 bg-white">
            {formData.featured_image_url ? (
              <div className="mb-8">
                <img src={formData.featured_image_url} alt={formData.title}
                  className="w-full aspect-video object-cover rounded-xl" />
              </div>
            ) : (
              <div className="mb-8 w-full h-48 rounded-xl bg-gray-100 flex flex-col items-center justify-center gap-2 text-gray-400">
                <ImageOff className="w-8 h-8" />
                <span className="text-xs">Nenhuma imagem de destaque</span>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mb-4">
              {selectedCategory && (
                <Badge style={{ backgroundColor: selectedCategory.color_hex, borderColor: selectedCategory.border_color_hex, color: "#000" }} className="border text-xs">
                  {selectedCategory.name}
                </Badge>
              )}
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                {new Date().toLocaleDateString("pt-BR", { year: "numeric", month: "long", day: "numeric" })}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <User className="w-3.5 h-3.5" />
                Dra Brenda Ramos
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 leading-tight">
              {formData.title || <span className="text-gray-300 italic">Título do post</span>}
            </h1>
            {formData.subtitle && (
              <h2 className="text-lg text-gray-600 mb-6 leading-snug">{formData.subtitle}</h2>
            )}
            {formData.excerpt && (
              <p className="text-gray-500 italic border-l-4 border-brand-primary/30 pl-4 mb-6 text-sm leading-relaxed">
                {formData.excerpt}
              </p>
            )}

            <div className="prose prose-lg max-w-none">
              {formData.content
                ? <BlogContent content={formData.content} />
                : <p className="text-gray-300 italic text-sm">Conteúdo do post aparecerá aqui...</p>
              }
            </div>

            {formData.video_url && (
              <div className="mt-8">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Vídeo</p>
                <VideoEmbed url={formData.video_url} />
              </div>
            )}
          </article>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`space-y-4 ${tab === "preview" ? "hidden" : ""}`}>
        <div>
          <Label htmlFor="title">Título</Label>
          <Input id="title" value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
        </div>

        <div>
          <Label htmlFor="subtitle">Subtítulo</Label>
          <Input id="subtitle" value={formData.subtitle}
            onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} />
        </div>

        <div>
          <Label htmlFor="excerpt">Resumo</Label>
          <Textarea id="excerpt" value={formData.excerpt}
            onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} rows={2} />
        </div>

        <div>
          <Label>Conteúdo</Label>
          <RichTextEditor
            value={formData.content}
            onChange={handleContentChange}
            placeholder="Escreva o conteúdo do post..."
            rows={12}
          />
        </div>

        {/* ── Vídeo ────────────────────────────────────────── */}
        <div className="border border-gray-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <Video className="w-4 h-4 text-brand-primary" />
            <span className="text-sm font-semibold text-gray-700">Vídeo do post</span>
            <span className="text-xs text-gray-400">(opcional)</span>
          </div>

          <div className="space-y-1">
            <Label htmlFor="video_url" className="text-xs text-gray-600">URL do vídeo</Label>
            <Input
              id="video_url"
              type="url"
              placeholder="https://youtu.be/... ou https://www.instagram.com/reel/..."
              value={formData.video_url || ""}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            />
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 space-y-1.5 text-xs text-blue-800">
            <p className="font-semibold text-[11px] uppercase tracking-wide text-blue-900">Como o vídeo é exibido</p>
            <p><span className="font-semibold">YouTube</span> — player embutido diretamente no post.</p>
            <p><span className="font-semibold">Instagram / TikTok</span> — botão que abre o vídeo na plataforma (essas redes não permitem incorporação).</p>
          </div>

          {formData.video_url && (
            <VideoEmbed url={formData.video_url} />
          )}
        </div>

        <div>
          <Label>Imagem em Destaque</Label>
          <div className="mt-2">
            <PhotoCropUpload
              value={formData.featured_image_url}
              onChange={url => setFormData(prev => ({ ...prev, featured_image_url: url }))}
              shape="rect"
              aspect={16 / 9}
              label="Imagem em Destaque"
              previewClassName="w-44 h-[99px] rounded-lg"
              postPreview
            />
          </div>
        </div>

        <div>
          <Label htmlFor="category">Categoria</Label>
          <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma categoria" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded border"
                      style={{ backgroundColor: category.color_hex, borderColor: category.border_color_hex }} />
                    {category.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <BlogPostScheduler
          key={editingPost?.id ?? "new"}
          scheduledFor={formData.scheduled_for}
          onScheduleChange={handleScheduleChange}
          disabled={false}
        />

        <div className="flex items-center space-x-2">
          <Switch id="published" checked={formData.published}
            onCheckedChange={(checked) => setFormData({ ...formData, published: checked })} />
          <Label htmlFor="published">Publicado</Label>
        </div>

        <Button type="submit" className="w-full" disabled={false}>
          <Plus className="w-4 h-4 mr-2" />
          {isEditing ? "Atualizar Post" : "Criar Post"}
        </Button>
      </form>

      {tab === "preview" && (
        <Button type="button" onClick={(e) => handleSubmit(e as any)}
          className="w-full bg-brand-primary hover:bg-brand-primary/90" disabled={false}>
          <Plus className="w-4 h-4 mr-2" />
          {isEditing ? "Atualizar Post" : "Criar Post"}
        </Button>
      )}
    </div>
  );
}
